/**
 * SSO auto-connect one-shot flag (PDR 011 / PDR 010 D3).
 *
 * An SSO identity reaching the zero-workspace connect leaf already has a
 * provider, so the app fires the data-scope grant for it automatically instead
 * of showing the ConnectPage chooser (that screen is for magic-link, which has
 * no provider). We must fire it at most ONCE per identity-session: if the user
 * denies at the provider, the backend 302s back here with no workspace, the leaf
 * is reached again, and an un-guarded auto-fire would loop forever.
 *
 * The flag lives in sessionStorage (not a React ref) precisely because the grant
 * is a same-window redirect — the page unloads and remounts, so a ref would
 * reset to false and re-fire. sessionStorage survives the same-tab navigation
 * and is cleared when the tab closes, so a genuinely new session can try again.
 */

// One definition of the key lives in lib/authStorage (so auth teardown can sweep
// it alongside the other non-user-scoped session flags). features → lib import.
import { SSO_AUTOCONNECT_ATTEMPTED_KEY } from "@/lib/authStorage";

const KEY = SSO_AUTOCONNECT_ATTEMPTED_KEY;

/** True once the SSO auto-connect grant has been fired this tab-session. */
export function ssoAutoConnectAttempted(): boolean {
  if (typeof window === "undefined") return false;
  try {
    return window.sessionStorage.getItem(KEY) === "1";
  } catch {
    return false;
  }
}

/** Mark the SSO auto-connect grant as fired (call BEFORE redirecting). */
export function markSsoAutoConnectAttempted(): void {
  if (typeof window === "undefined") return;
  try {
    window.sessionStorage.setItem(KEY, "1");
  } catch {
    /* sessionStorage unavailable (private mode / blocked) — best-effort guard */
  }
}

/**
 * Clear the one-shot flag so the next sign-in's connect-first flow auto-redirects
 * again. Called on auth teardown (logout / unauthenticated transition) — the flag
 * is per-tab-session, not per-identity, so it must reset when the identity ends.
 */
export function clearSsoAutoConnectAttempted(): void {
  if (typeof window === "undefined") return;
  try {
    window.sessionStorage.removeItem(KEY);
  } catch {
    /* best-effort */
  }
}
